import React, { useCallback, useEffect, useState } from 'react';

import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';
import { useToast } from '../../../../hooks/toast';
import { colors } from '../../../../styles/tokens';
import { AdminAppVersionsService } from '../../../../services/AdminAppVersionsService';
import { IAppVersion } from '../../../../domains/AppVersion';

import {
  AppSection,
  AppSectionHeader,
  AppSectionTag,
  AppSectionTitle,
  Container,
  FieldGroup,
  FieldLabel,
  FieldRow,
  Header,
  LinkInputRow,
  Panel,
  PanelBadge,
  PanelFooter,
  PanelHeader,
  PanelTitle,
  PageSubtitle,
  PageTitle,
  PlatformGrid,
  SaveButton,
  TestLinkButton,
  TextInput,
  UpdatedAtLabel,
} from './styles';

type AppKey = 'student' | 'staff';
type PlatformKey = 'android' | 'ios';

interface IEditedValue {
  min_version: string;
  latest_version: string;
  update_url: string;
}

const APPS: Array<{ key: AppKey; label: string }> = [
  { key: 'student', label: 'App do aluno' },
  { key: 'staff', label: 'App da equipe' },
];

const PLATFORMS: Array<{ key: PlatformKey; label: string; icon: string }> = [
  { key: 'android', label: 'Android', icon: 'android' },
  { key: 'ios', label: 'iOS', icon: 'phone_iphone' },
];

const VERSION_PATTERN = /^\d+(\.\d+){0,3}$/;

const toEditedValue = (appVersion: IAppVersion): IEditedValue => ({
  min_version: appVersion.min_version,
  latest_version: appVersion.latest_version,
  update_url: appVersion.update_url || '',
});

const AdminAppVersions: React.FC = () => {
  const adminAppVersionsService = new AdminAppVersionsService();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [appVersions, setAppVersions] = useState<IAppVersion[]>([]);
  const [editedValues, setEditedValues] = useState<
    Record<string, IEditedValue>
  >({});
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    adminAppVersionsService
      .getAll()
      .then(response => {
        setAppVersions(response.data);
        setEditedValues(
          Object.fromEntries(
            response.data.map(item => [item.id, toEditedValue(item)]),
          ),
        );
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro ao buscar as versões dos apps.',
        });
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findAppVersion = useCallback(
    (app: AppKey, platform: PlatformKey) =>
      appVersions.find(item => item.app === app && item.platform === platform),
    [appVersions],
  );

  const handleChange = useCallback(
    (id: string, field: keyof IEditedValue, value: string) => {
      setEditedValues(current => ({
        ...current,
        [id]: { ...current[id], [field]: value },
      }));
    },
    [],
  );

  const isDirty = useCallback(
    (appVersion: IAppVersion): boolean => {
      const edited = editedValues[appVersion.id];
      if (!edited) return false;

      return (
        edited.min_version !== appVersion.min_version ||
        edited.latest_version !== appVersion.latest_version ||
        edited.update_url !== (appVersion.update_url || '')
      );
    },
    [editedValues],
  );

  const handleTestLink = useCallback(
    (id: string) => {
      const url = editedValues[id]?.update_url?.trim();
      if (!url) return;

      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [editedValues],
  );

  const handleSave = useCallback(
    (appVersion: IAppVersion) => {
      const edited = editedValues[appVersion.id];
      if (!edited) return;

      const minVersion = edited.min_version.trim();
      const latestVersion = edited.latest_version.trim();

      if (
        !VERSION_PATTERN.test(minVersion) ||
        !VERSION_PATTERN.test(latestVersion)
      ) {
        addToast({
          type: 'error',
          title: 'Versão inválida.',
          description: 'Use o formato numérico, ex.: 1.2.0.',
        });
        return;
      }

      setSavingId(appVersion.id);
      adminAppVersionsService
        .update(appVersion.id, {
          min_version: minVersion,
          latest_version: latestVersion,
          update_url: edited.update_url.trim() || null,
        })
        .then(response => {
          setAppVersions(current =>
            current.map(item =>
              item.id === appVersion.id ? response.data : item,
            ),
          );
          setEditedValues(current => ({
            ...current,
            [appVersion.id]: toEditedValue(response.data),
          }));
          addToast({ type: 'success', title: 'Versão atualizada.' });
        })
        .catch(err => {
          addToast({
            type: 'error',
            title: 'Erro ao salvar.',
            description:
              err?.response?.data?.message || 'Tente novamente em instantes.',
          });
        })
        .finally(() => setSavingId(null));
    },
    [addToast, editedValues],
  );

  return (
    <Painel loading={loading}>
      <Container>
        <Header>
          <div>
            <PageTitle>Gerenciamento de loja</PageTitle>
            <PageSubtitle>
              Versão mínima aceita, versão mais recente e link da loja para cada
              app e plataforma. Versão instalada abaixo da mínima bloqueia o uso
              até atualizar.
            </PageSubtitle>
          </div>
        </Header>

        {APPS.map(app => (
          <AppSection key={app.key}>
            <AppSectionHeader>
              <AppSectionTitle>{app.label}</AppSectionTitle>
              <AppSectionTag>{app.key}</AppSectionTag>
            </AppSectionHeader>

            <PlatformGrid>
              {PLATFORMS.map(platform => {
                const appVersion = findAppVersion(app.key, platform.key);

                if (!appVersion) {
                  return <Panel key={platform.key} />;
                }

                const edited =
                  editedValues[appVersion.id] || toEditedValue(appVersion);
                const dirty = isDirty(appVersion);
                const saving = savingId === appVersion.id;

                return (
                  <Panel key={appVersion.id}>
                    <PanelHeader>
                      <Icon
                        name={platform.icon}
                        size={20}
                        color={colors.onSurfaceVariant}
                      />
                      <PanelTitle>{platform.label}</PanelTitle>
                      <PanelBadge tone={dirty ? 'warning' : 'ok'}>
                        {dirty ? 'Não salvo' : 'Salvo'}
                      </PanelBadge>
                    </PanelHeader>

                    <FieldRow>
                      <FieldGroup>
                        <FieldLabel htmlFor={`min-version-${appVersion.id}`}>
                          Versão mínima
                        </FieldLabel>
                        <TextInput
                          id={`min-version-${appVersion.id}`}
                          placeholder="1.0.0"
                          value={edited.min_version}
                          onChange={e =>
                            handleChange(
                              appVersion.id,
                              'min_version',
                              e.target.value,
                            )
                          }
                        />
                      </FieldGroup>

                      <FieldGroup>
                        <FieldLabel htmlFor={`latest-version-${appVersion.id}`}>
                          Versão mais recente
                        </FieldLabel>
                        <TextInput
                          id={`latest-version-${appVersion.id}`}
                          placeholder="1.0.0"
                          value={edited.latest_version}
                          onChange={e =>
                            handleChange(
                              appVersion.id,
                              'latest_version',
                              e.target.value,
                            )
                          }
                        />
                      </FieldGroup>
                    </FieldRow>

                    <FieldGroup>
                      <FieldLabel htmlFor={`update-url-${appVersion.id}`}>
                        Link da loja
                      </FieldLabel>
                      <LinkInputRow>
                        <TextInput
                          id={`update-url-${appVersion.id}`}
                          placeholder="https://..."
                          style={{ flex: 1 }}
                          value={edited.update_url}
                          onChange={e =>
                            handleChange(
                              appVersion.id,
                              'update_url',
                              e.target.value,
                            )
                          }
                        />
                        <TestLinkButton
                          type="button"
                          title="Abrir link em uma nova aba"
                          disabled={!edited.update_url.trim()}
                          onClick={() => handleTestLink(appVersion.id)}
                        >
                          <Icon name="open_in_new" size={18} />
                        </TestLinkButton>
                      </LinkInputRow>
                    </FieldGroup>

                    <PanelFooter>
                      <UpdatedAtLabel>
                        Atualizado em{' '}
                        {new Date(appVersion.updated_at).toLocaleDateString(
                          'pt-BR',
                        )}
                      </UpdatedAtLabel>
                      <SaveButton
                        type="button"
                        name={`save-${appVersion.id}`}
                        value="Salvar"
                        loading={saving}
                        disabled={!dirty || saving}
                        onClick={() => handleSave(appVersion)}
                      />
                    </PanelFooter>
                  </Panel>
                );
              })}
            </PlatformGrid>
          </AppSection>
        ))}
      </Container>
    </Painel>
  );
};

export { AdminAppVersions };
